import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import { ReactNode } from 'react';

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthUser {
  id: number;
  email: string;
  is_admin: boolean;
  is_super_admin?: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  is_admin: boolean;
  is_super_admin: boolean;
  fetchUserProfile: (skipTokenCheck?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export const AuthProvider = ({
  children
}: AuthProviderProps) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const fetchUserProfile = async (skipTokenCheck = false) => {
    try {
      if (!token) {
        console.log('No token found in fetchUserProfile');
        setUser(null);
        setLoading(false);
        return;
      }

      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('User profile response:', response.data);
      const profile = response.data.user;

      const updatedUser = {
        ...profile,
        is_admin: profile.is_admin === true,
        is_super_admin: profile.is_super_admin === true
      };

      setUser(updatedUser);
      console.log('Loaded user:', updatedUser);
      setError(null);
    } catch (error: unknown) {
      console.error('Error fetching user profile:', error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          const errorMessage = error.response?.data?.message || '';

          // Only logout if it's a token-related error
          if (
            errorMessage.includes('token') ||
            errorMessage.includes('expired') ||
            errorMessage.includes('invalid') ||
            errorMessage === 'Unauthorized' ||
            !errorMessage
          ) {
            console.log(
              'Token expired or invalid in fetchUserProfile, clearing auth state'
            );
            handleLogout();
          } else {
            console.log(
              '401 error in fetchUserProfile is business logic related, not logging out:',
              errorMessage
            );
          }
        } else if (token) {
          setError(`Failed to fetch user profile: ${error.message}`);
        }
      } else if (token) {
        setError('Failed to fetch user profile');
      }
    } finally {
      setLoading(false);
    }
  };

  // Set up axios interceptors
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          console.log('Adding token to request:', config.url);
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        console.error('Response interceptor error:', error);
        
        // Only logout on 401 errors that indicate token expiration
        // Don't logout on business logic 401 errors (like incorrect password)
        if (error.response?.status === 401 && token) {
          const errorMessage = error.response?.data?.message || '';
          
          // Only logout if it's a token-related error, not a business logic error
          if (errorMessage.includes('token') || 
              errorMessage.includes('expired') || 
              errorMessage.includes('invalid') ||
              errorMessage === 'Unauthorized' ||
              !errorMessage) {
            console.log('Token expired in interceptor, clearing auth state...');
            handleLogout();
          } else {
            console.log('401 error is business logic related, not logging out:', errorMessage);
          }
        }
        
        return Promise.reject(error);
      }
    );

    //Temporarily don't fetchUserProfile(); and  setLoading(false);
    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token]);

  const handleLogout = () => {
    console.log('Handling logout...');
    // Remove all relevant localStorage/sessionStorage items
    localStorage.removeItem('token');
    localStorage.removeItem('guest_cart');
    sessionStorage.clear(); // Clear all session storage (or selectively if you prefer)
    setToken(null);
    setUser(null);
    setError(null);
  };

  console.log(
  'Login request URL:',
  `${import.meta.env.VITE_API_URL}/auth/login`
);
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/login', {
        email,
        password
      });
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      
      // Ensure is_admin is properly set from the response
      const updatedUser = {
        ...user,
        is_admin: user.is_admin === true,
        is_super_admin: user.is_super_admin === true
      };
      
      setUser(updatedUser);
      setError(null);
      return true;
    } catch (error: unknown) {
      console.error('Login error:', error);

      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || 'Error logging in';
        setError(message);
      } else {
        setError('Error logging in');
      }

      return false;
    }
    finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    token,
    login,
    logout: handleLogout,
    is_admin: user?.is_admin || false,
    is_super_admin: user?.is_super_admin || false,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 